import { Maximize2, X } from "lucide-react";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";

import { useAreaRecordGeoItems } from "~/features/recordGeoItems/hooks";
import { v2Route } from "~/v2/routes";
import { AreaGeoItemDetail } from "./AreaGeoItemDetail";
import { AreaGeoItemsList } from "./AreaGeoItemsList";

interface AreaPeekProps {
  /** When set, the sheet is open and shows the record's area. */
  recordId: string | null;
  /** Pre-selected geo-item. */
  geoItemId?: string;
  onClose: () => void;
}

/**
 * Separate read-only area panel (V1's second peek), opened from the entity "Area"
 * action. The maximize icon opens the full area-input page.
 */
export function AreaPeek({ recordId, geoItemId, onClose }: AreaPeekProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openFullPage = () => {
    if (!recordId) return;
    onClose();
    navigate(
      geoItemId
        ? v2Route.areaInput.record.detail.$buildPath({ params: { recordId, geoItemId } })
        : v2Route.areaInput.record.$buildPath({ params: { recordId } }),
    );
  };

  return (
    <Sheet open={recordId !== null} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" showClose={false} className="flex flex-col p-0">
        <SheetHeader className="flex flex-row items-center justify-between gap-2">
          <SheetTitle className="capitalize">
            {t("feature.entities.detail.areaInput", { defaultValue: "Area Input" })}
          </SheetTitle>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={openFullPage}
                  aria-label={t("ui.openInFullPage", { defaultValue: "Open full page" })}
                >
                  <Maximize2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.openInFullPage", { defaultValue: "Open full page" })}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label={t("ui.close", { defaultValue: "Close" })}
                >
                  <X />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.close", { defaultValue: "Close" })}</TooltipContent>
            </Tooltip>
          </div>
        </SheetHeader>
        <SheetBody>
          <Suspense fallback={<DetailLoading />}>
            {recordId !== null && <AreaPeekBody recordId={recordId} geoItemId={geoItemId} />}
          </Suspense>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function AreaPeekBody({ recordId, geoItemId }: { recordId: string; geoItemId?: string }) {
  const { t } = useTranslation();
  const { data } = useAreaRecordGeoItems(recordId);
  const recordGeoItems = data.records[0]?.recordGeoItems ?? [];

  const firstActive = recordGeoItems.find((rgi) => !rgi.deactivatedAt) ?? recordGeoItems[0];
  const [selected, setSelected] = useState<string | null>(geoItemId ?? firstActive?.geoItemId ?? null);

  if (recordGeoItems.length === 0) return <p className="text-[13px] text-fg-3">{t("feature.common.noData")}</p>;

  return (
    <div className="grid grid-cols-[180px_minmax(0,1fr)] gap-4">
      <div className="min-w-0 border-r border-border-default pr-3">
        <AreaGeoItemsList recordGeoItems={recordGeoItems} selectedGeoItemId={selected} onSelect={setSelected} />
      </div>
      <div className="min-w-0">
        {selected ? (
          <Suspense fallback={<DetailLoading />}>
            <AreaGeoItemDetail recordId={recordId} geoItemId={selected} />
          </Suspense>
        ) : (
          <p className="text-[13px] text-fg-3">{t("feature.common.noData")}</p>
        )}
      </div>
    </div>
  );
}
